import { useCallback, useRef } from "react";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import {
	extractSSEEventName,
	isControlFrame,
	isDeltaFrame,
	isReconnect,
	isStreamEnd,
	parseSSEEvent,
	splitSSELines,
} from "@/lib/sse-parser";
import {
	getActiveAgentJob,
	listChatMessages,
	listQueuedChatMessages,
	loadChat,
	stopAgentJob,
} from "@/lib/api";
import {
	type ChatListEntry,
	type ChatMessageDto,
	type ChatMetadata,
	ChatStopReason,
	parseRetractedMessage,
	type RetractedMessage,
} from "@/lib/chat";
import { API_URL, handleDeniedResponse } from "@/lib/api-client";
import { openEnvironmentFileByPath, refreshEnvironmentFiles } from "@/lib/environment-files";
import { agentOpenFilePath } from "@/lib/agent-open-file";
import { refreshCloudSharedVolumes } from "@/stores/useCloudSharedVolumeStore";
import { agentChangedKnowledgeGraph } from "@/lib/knowledge-graph";
import { useKnowledgeGraphStore } from "@/stores/useKnowledgeGraphStore";
import { refreshDeviceSharedVolumes } from "@/stores/useDeviceSharedVolumeStore";
import { extractContentText, parseMessageControls } from "@/lib/message-controls";
import { isStreamFrameApplicable } from "@/lib/stream-guard";
import { useToastStore } from "@/stores/useToastStore";
import { findPendingHITL } from "@/components/hitl/hitl";
import { useChatDraftStore } from "@/stores/useChatDraftStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { resetSearch } from "@/lib/workspace-search";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";

function queueRetractionRestore(chatId: string, text: string | null | undefined): void {
	if (!text) return;
	useChatDraftStore.getState().queueDraftRestore(chatId, parseMessageControls(text).text);
}

async function resyncAfterStopFinalized(chatId: string, restoreText: string | null): Promise<void> {
	queueRetractionRestore(chatId, restoreText);
	try {
		const messages = await listChatMessages(chatId);
		if (useChatStore.getState().chatId === chatId) {
			useChatStore.getState().setMessages(messages);
		}
	} catch (err: unknown) {
		useToastStore.getState().show({
			type: "error",
			message: "停止完了後のメッセージ再同期に失敗しました。再読み込みしてください。",
		});
		throw err;
	}
}

/** チャットを開いたとき最初に取得する件数。1 画面に収まる程度にして、残りは表示後に取得する。 */
const INITIAL_MESSAGE_PAGE_SIZE = 50;

async function loadOlderMessages(
	chatId: string,
	before: number,
	isCurrent: () => boolean,
): Promise<void> {
	try {
		const older = await listChatMessages(chatId, { before });
		if (isCurrent()) useChatStore.getState().prependOlderMessages(older);
	} catch (err: unknown) {
		if (isCurrent()) {
			useChatStore.getState().setChatLoadFailed(true);
			useToastStore.getState().show({
				type: "error",
				message: "過去のメッセージの読み込みに失敗しました。再読み込みしてください。",
			});
		}
		throw err;
	}
}

function latestLocalUserTextForRetraction(): string | null {
	const store = useChatStore.getState();
	// 停止時の撤回はバックエンドの「生の末尾が user」の契約と合わせるため、
	// lastConversationMessage には変えない。
	const tail = store.messages.at(-1);
	if (tail?.role !== "user") return null;
	if (tail.createdById !== useCurrentUserStore.getState().id) return null;
	const text = parseMessageControls(extractContentText(tail.content)).text;
	return text.trim() === "" ? null : text;
}

function connectionLost(message = "接続が切れました。再接続しています。"): void {
	useToastStore.getState().show({
		type: "error",
		message,
	});
}

/**
 * アクティブなチャットの SSE 接続を管理する。
 * バックエンドは agent turn または monitor 由来の live activity がある間だけ接続を維持し、
 * アイドル時は `streamEnd` で正常終了する。アイドルチャットの受動閲覧中は、他者の後続更新は
 * リロードまたは開き直しまで反映されない。
 */
export function useAgentSession() {
	const abortRef = useRef<AbortController | null>(null);
	const chatIdRef = useRef<string | null>(null);
	/**
	 * Session generation counter. Bumped every time the active session changes
	 * (new thread via startNewChatDraft, chat swap via switchToChat). The reader loop
	 * checks this against the epoch captured at startStreaming time and drops
	 * tail-end buffered bytes after abort() so old-session events cannot leak
	 * into a freshly-restored chat. See issue #60.
	 */
	const sessionEpochRef = useRef(0);
	/** 停止中に進行中ジョブを監視するポーリングのタイマー。多重起動を防ぐためにここで保持する。 */
	const stopPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const stopPollGenerationRef = useRef(0);
	const pendingStopRestoreRef = useRef<{ chatId: string; text: string | null } | null>(null);

	/** 停止監視ポーリングを止める。 */
	const clearStopPoll = useCallback(() => {
		stopPollGenerationRef.current++;
		if (stopPollRef.current !== null) {
			clearTimeout(stopPollRef.current);
			stopPollRef.current = null;
		}
	}, []);

	const takeLocalStopRestore = useCallback((chatId: string): string | null => {
		const pending = pendingStopRestoreRef.current;
		if (pending?.chatId !== chatId) return null;
		pendingStopRestoreRef.current = null;
		return pending.text;
	}, []);

	const handleStopFinalized = useCallback(
		async (chatId: string, retracted: RetractedMessage | null): Promise<void> => {
			clearStopPoll();
			const localRestoreText = retracted === null ? null : takeLocalStopRestore(chatId);
			try {
				await resyncAfterStopFinalized(chatId, localRestoreText);
			} catch {
				// resyncAfterStopFinalized がユーザー通知したうえで投げる。SSE ループ自体は継続する。
			}
		},
		[clearStopPoll, takeLocalStopRestore],
	);

	/**
	 * 停止中 (status === "stopping") に進行中ジョブが消えるまで定期的に確認する。
	 * SSE が切れていても、ジョブが消えたら (= 停止処理が完了したら) メッセージと
	 * ワークスペースファイルを取り直して idle に戻す。停止完了の systemNotice が SSE で
	 * 先に届いた場合はそちらが先に idle へ戻すため、本ポーリングは何もせず終わる。
	 */
	const pollActiveJobUntilFinalized = useCallback(
		(chatId: string) => {
			clearStopPoll();
			const generation = stopPollGenerationRef.current;
			const tick = async () => {
				if (generation !== stopPollGenerationRef.current) return;
				if (useChatStore.getState().chatId !== chatId) {
					clearStopPoll();
					return;
				}
				let job: Awaited<ReturnType<typeof getActiveAgentJob>>;
				try {
					job = await getActiveAgentJob(chatId);
				} catch (err: unknown) {
					useToastStore.getState().show({
						type: "error",
						message: "停止状態の確認に失敗しました。再接続を待っています。",
					});
					console.error("停止監視のジョブ取得に失敗しました", err);
					if (generation === stopPollGenerationRef.current) {
						stopPollRef.current = setTimeout(tick, 1500);
					}
					return;
				}
				if (generation !== stopPollGenerationRef.current) return;
				if (job !== null) {
					stopPollRef.current = setTimeout(tick, 1500);
					return;
				}
				clearStopPoll();
				if (useChatStore.getState().chatId !== chatId) return;
				try {
					const messages = await listChatMessages(chatId);
					if (useChatStore.getState().chatId !== chatId) return;
					useChatStore.getState().setMessages(messages);
					const tail = lastConversationMessage(messages);
					if (tail?.role === "systemNotice") {
						const retracted = parseRetractedMessage(tail.stopDetails);
						if (retracted !== null) queueRetractionRestore(chatId, takeLocalStopRestore(chatId));
					}
				} catch (err: unknown) {
					useToastStore.getState().show({
						type: "error",
						message: "停止完了後のメッセージ取得に失敗しました。再読み込みしてください。",
					});
					console.error("停止完了後のメッセージ取得に失敗しました", err);
					return;
				}
				const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
				if (environmentId !== null) {
					refreshEnvironmentFiles(environmentId);
					refreshDeviceSharedVolumes(environmentId);
					refreshCloudSharedVolumes(environmentId);
				}
				useChatStore.getState().setStatus("idle");
				useChatStore.getState().setActiveAgentJob(null);
			};
			stopPollRef.current = setTimeout(tick, 1500);
		},
		[clearStopPoll, takeLocalStopRestore],
	);

	/**
	 * Synchronously cancel every in-flight piece of work tied to the current
	 * session before establishing a new session identity (startNewChatDraft,
	 * switchToChat). Order matters:
	 *   1. Invalidate chatIdRef FIRST so processEvent's expectedChatId
	 *      guard drops stale events.
	 *   2. Abort the SSE reader.
	 *   3. Bump session epoch LAST so async reader-loop iterations observe the
	 *      mismatch and drop their buffered events.
	 */
	const tearDownCurrentSession = useCallback(() => {
		chatIdRef.current = null;
		abortRef.current?.abort();
		abortRef.current = null;
		sessionEpochRef.current++;
		clearStopPoll();
		useChatStore.getState().clearStreamingAssistant();
	}, [clearStopPoll]);

	/**
	 * 現在の SSE ストリームだけを中断する (チャット状態・epoch は保持)。承認決定直後など、
	 * REST での再同期の前に旧ストリームの配信と競合しないよう先に止めるために使う。
	 */
	const stopStreaming = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
	}, []);

	const startStreaming = useCallback(
		async (chatId: string, startFrom?: number, options?: { markStreaming?: boolean }) => {
			const store = useChatStore.getState();
			// handleSend awaits the POST before calling us — if the user switched
			// chats during that window, the active chat has moved on and taking over
			// the UI here would surface a "考察中" indicator on a chat the user is
			// no longer sending from.
			if (store.chatId !== chatId) return;

			// ファイル一覧の最新化はチャットではなく環境単位でキーする。ストリーム開始時点の
			// 環境を固定しておき、ターン中の取得結果がこの環境のものかどうかを判定する。
			const environmentId = selectEffectiveEnvironmentId(store);

			// 現在ストア上にあるテキストメッセージの最大 OrderIndex を取って、
			// そこから先のメッセージだけを SSE で受け取る。呼び出し側が楽観追加
			// したユーザーメッセージが最新の order を占めているため、結果として
			// エージェント応答以降だけがストリームから流れてくる。
			startFrom ??=
				store.messages.length === 0 ? -1 : Math.max(...store.messages.map((x) => x.order));

			chatIdRef.current = chatId;
			const epochAtStart = sessionEpochRef.current;

			if (options?.markStreaming ?? true) {
				store.setStatus("streaming");
			}

			abortRef.current?.abort();
			const controller = new AbortController();
			abortRef.current = controller;

			// epoch (チャット切替) に加え、このストリーム自身の abort も見る。同一チャット内の
			// stop / 再 start では epoch が進まないため、abort 済み reader の解決済み read() 結果や
			// バッファ済みフレームを適用しないための判定。
			const isStaleEpoch = () =>
				!isStreamFrameApplicable({
					epochAtStart,
					currentEpoch: sessionEpochRef.current,
					aborted: controller.signal.aborted,
				});

			let retryAttempt = 0;
			let notifiedRetry = false;
			while (!isStaleEpoch()) {
				try {
					const url = new URL(`${API_URL}/api/chats/${chatId}/stream`, window.location.origin);
					url.searchParams.set("startFrom", String(startFrom));
					const res = await fetch(url, {
						signal: controller.signal,
						credentials: "include",
						headers: { Accept: "text/event-stream" },
					});

					if (!res.ok || !res.body) {
						if (await handleDeniedResponse(res)) {
							useChatStore.getState().setStatus("idle");
							return;
						}
						const body = await res.text();
						console.error(body);
						if (!isStaleEpoch()) {
							if (res.status === 401 || res.status === 404) {
								useChatStore.getState().setStatus("idle");
								connectionLost(
									res.status === 401 ? "ログインが必要です。" : "チャットが見つかりません。",
								);
								return;
							}
							throw new Error(`SSE failed with status ${res.status}: ${body}`);
						}
						return;
					}

					retryAttempt = 0;
					notifiedRetry = false;
					const reader = res.body.getReader();
					const decoder = new TextDecoder();
					let buffer = "";
					// `event:` 行は値を持たないため、直後の `data:` 行を解釈するまで名前を持ち越す。
					let pendingEventName: string | null = null;
					let reconnectRequested = false;

					while (true) {
						const { done, value } = await reader.read();
						if (done) break;
						if (isStaleEpoch()) break;

						const chunk = decoder.decode(value, { stream: true });
						const { lines, remaining } = splitSSELines(chunk, buffer);
						buffer = remaining;

						for (const line of lines) {
							if (isStaleEpoch()) break;
							const eventName = extractSSEEventName(line);
							const frame = parseSSEEvent(line, pendingEventName);
							pendingEventName = eventName;
							if (!frame) continue;
							if (isReconnect(frame)) {
								reconnectRequested = true;
								continue;
							}
							if (isControlFrame(frame)) {
								if (
									isStreamEnd(frame) &&
									!isStaleEpoch() &&
									useChatStore.getState().chatId === chatId
								) {
									useChatStore.getState().clearStreamingAssistant();
									useChatStore.getState().setStatus("idle");
								}
								return;
							}
							if (isDeltaFrame(frame)) {
								if (useChatStore.getState().chatId === chatId) {
									useChatStore.getState().applyStreamDelta(frame.delta);
								}
								continue;
							}
							const terminal = processEvent(frame, {
								expectedChatId: chatId,
								onTurnTerminal: (terminalDto, retracted) => {
									if (retracted !== null) void handleStopFinalized(terminalDto.chatId, retracted);
									else clearStopPoll();
								},
							});
							startFrom = Math.max(startFrom ?? -1, frame.order);
							if (terminal && !isStaleEpoch() && environmentId !== null) {
								refreshEnvironmentFiles(environmentId);
								refreshDeviceSharedVolumes(environmentId);
								refreshCloudSharedVolumes(environmentId);
							}
						}
					}

					if (reconnectRequested) {
						// 停止中の Pod に振られ続けても即時の再接続を繰り返さないよう、間隔を空けて散らす。
						const delayMs = 500 + Math.random() * 1000;
						await new Promise((resolve) => setTimeout(resolve, delayMs));
						continue;
					}
					if (!isStaleEpoch()) {
						throw new Error("SSE stream closed unexpectedly");
					}
				} catch (err: unknown) {
					// AbortError is expected on chat-switch teardown or when a newer stream takes over.
					if (err instanceof DOMException && err.name === "AbortError") {
						return;
					}

					console.error(err);

					if (chatIdRef.current !== chatId || isStaleEpoch()) {
						return;
					}

					if (!notifiedRetry) {
						// 停止処理中の切断は紛らわしい「接続が切れました」通知を出さず、進行中ジョブの
						// 完了をポーリングして状態を整える (SSE は長時間維持されるため通常は起きない)。
						if (useChatStore.getState().status === "stopping") {
							pollActiveJobUntilFinalized(chatId);
						} else {
							connectionLost();
						}
						notifiedRetry = true;
					}
					const delayMs = Math.min(30_000, 1000 * 2 ** retryAttempt);
					retryAttempt++;
					await new Promise((resolve) => setTimeout(resolve, delayMs));
				}
			}
		},
		[pollActiveJobUntilFinalized, handleStopFinalized, clearStopPoll],
	);

	/**
	 * 現在のセッションを破棄し、空の「新規チャット下書き」状態にする。
	 * バックエンドにはチャットを作らず、チャット一覧にも追加しない —
	 * 実際のチャット作成は最初のメッセージ送信時 (CenterPane.handleSend) に行う。
	 * これにより、新規チャットボタンを押すたびに空のチャットが溜まるのを防ぐ。
	 */
	const startNewChatDraft = useCallback(() => {
		tearDownCurrentSession();
		useChatStore.getState().reset();
		resetSearch();
		useTranscriptSearchStore.getState().reset();
		chatIdRef.current = null;
	}, [tearDownCurrentSession]);

	const switchToChat = useCallback(
		async (entry: ChatMetadata | ChatListEntry): Promise<void> => {
			tearDownCurrentSession();
			const store = useChatStore.getState();
			const previousEnvironmentId = store.environmentId;
			const previousEnvironmentFileChildren = store.environmentFileChildren;
			const previousOpenFileEntries = store.openFileEntries;
			const metadata = switchedChatMetadata(entry, store);
			// 別チャットを開くので、まずメッセージ等を初期化してからメタデータを載せる。
			store.reset();
			resetSearch();
			useTranscriptSearchStore.getState().reset();
			store.setChatMetadata(metadata);
			// 同じ環境のチャット間を切り替える場合は読み込み済みツリーをそのまま残し、
			// 再取得が完了するまでのちらつきを防ぐ。違う環境なら reset で
			// environmentFileChildren が null になり、再取得までスケルトンが出る。
			if (
				previousEnvironmentId === metadata.environmentId &&
				previousEnvironmentFileChildren !== null
			) {
				store.adoptEnvironmentFiles(previousEnvironmentFileChildren, previousOpenFileEntries);
			}
			// localStorage に保存済みの分割レイアウトがあれば復元する (無ければ何もしない)。
			store.restoreEditorLayout(metadata.id);
			chatIdRef.current = metadata.id;

			const epochAtStart = sessionEpochRef.current;
			store.setLoadingChat(true);
			// 履歴の取得を待たずに既存のワークスペースツリーを表示する。
			// 環境が削除された孤児チャットにはツリーが存在しない。
			if (metadata.environmentId !== null) {
				refreshEnvironmentFiles(metadata.environmentId);
			}
			try {
				// 権威的なメタデータを取得して revision と hasLiveActivity を確定する。
				const [page, fresh, queued] = await Promise.all([
					listChatMessages(metadata.id, { limit: INITIAL_MESSAGE_PAGE_SIZE }),
					loadChat(metadata.id),
					listQueuedChatMessages(metadata.id),
				]);
				if (sessionEpochRef.current !== epochAtStart) return;
				const hasOlder = page.length === INITIAL_MESSAGE_PAGE_SIZE;
				let messages = page;
				if (hasOlder && lastConversationMessage(page) === undefined) {
					// 末尾が監視・サブエージェントのイベントだけだと後段の再開判定ができないため、全件を待つ。
					messages = [...(await listChatMessages(metadata.id, { before: page[0].order })), ...page];
					if (sessionEpochRef.current !== epochAtStart) return;
				}
				useChatStore.getState().setMessages(messages);
				if (hasOlder && messages === page) {
					void loadOlderMessages(
						metadata.id,
						page[0].order,
						() => sessionEpochRef.current === epochAtStart,
					);
				}
				// 昇格をまたぐ取得で二重表示しないよう、既に messages に居る項目を clientRequestId で除外する。
				const knownRequestIds = new Set(
					messages.map((m) => m.clientRequestId).filter((id): id is string => id != null),
				);
				useChatStore
					.getState()
					.setQueuedMessages(queued.filter((q) => !knownRequestIds.has(q.clientRequestId)));
				if (fresh) {
					useChatStore.getState().setChatMetadata(fresh);
				}
				let activeJob: Awaited<ReturnType<typeof getActiveAgentJob>> = null;
				try {
					activeJob = await getActiveAgentJob(metadata.id);
				} catch (err: unknown) {
					useToastStore.getState().show({
						type: "error",
						message: "進行中ジョブの取得に失敗しました。停止ボタンの状態が古い可能性があります。",
					});
					console.error("進行中ジョブの取得に失敗しました", err);
				}
				if (sessionEpochRef.current !== epochAtStart) return;
				// 進行中ジョブがあれば停止ボタンの対象として控える。
				useChatStore.getState().setActiveAgentJob(activeJob?.id ?? null);
				// 質問の途中でチャットを開き直した場合に HITL パネルを復元する。
				const pendingHITL = findPendingHITL(messages);
				const conversationTail = lastConversationMessage(messages);
				if (pendingHITL) {
					useChatStore.getState().setHITL(pendingHITL);
				} else if (
					activeJob?.status === "cancelRequested" &&
					(conversationTail === undefined || !isTurnTerminal(conversationTail))
				) {
					// 停止要求済みのまま開き直した: 停止中表示にし、停止完了の systemNotice を待つ。
					useChatStore.getState().setStatus("stopping");
					startStreaming(metadata.id, undefined, { markStreaming: false });
				} else if (shouldResumeActiveTurnOnOpen(activeJob?.status, conversationTail)) {
					startStreaming(metadata.id);
				} else if (!metadata.archived && fresh?.hasLiveActivity === true) {
					startStreaming(metadata.id, undefined, { markStreaming: false });
				}
			} catch (err: unknown) {
				// 読み込み失敗を「HITL 解決済み」と誤認されないよう記録する。既に次の切替が
				// 始まっていれば、その切替の結果に委ねる。
				if (sessionEpochRef.current === epochAtStart) {
					useChatStore.getState().setChatLoadFailed(true);
				}
				useToastStore.getState().show({
					type: "error",
					message: "チャット履歴の読み込みに失敗しました。",
				});
				throw err;
			} finally {
				if (sessionEpochRef.current === epochAtStart) {
					useChatStore.getState().setLoadingChat(false);
				}
			}
		},
		[tearDownCurrentSession, startStreaming],
	);

	/**
	 * 進行中のエージェントジョブの停止を要求する。停止ボタンから呼ぶ。
	 * 状態を一旦 "stopping" にして停止 API を呼び、Pending の即時 cancelled なら
	 * メッセージとワークスペースを取り直して idle へ戻す。Running の cancelRequested は
	 * 停止完了の systemNotice (SSE) を待つため "stopping" を維持する。
	 * 失敗時はトーストを出し、進行中ジョブを取り直して状態を整える。
	 */
	const stopAgentSession = useCallback(async (): Promise<void> => {
		const chatId = useChatStore.getState().chatId;
		if (!chatId) return;

		pendingStopRestoreRef.current = { chatId, text: latestLocalUserTextForRetraction() };
		useChatStore.getState().setStatus("stopping");

		// 停止対象は実際の進行中ジョブを source of truth とする。HITL 応答後など、store の
		// activeAgentJobId が前ターンの終端済みジョブのまま古くなっていることがあるため、まず取り直す。
		let jobId: string | null;
		try {
			const active = await getActiveAgentJob(chatId);
			if (useChatStore.getState().chatId !== chatId) return;
			jobId = active?.id ?? null;
			useChatStore.getState().setActiveAgentJob(jobId);
		} catch (err: unknown) {
			useToastStore.getState().show({
				type: "error",
				message: "進行中ジョブの取得に失敗しました。表示中の状態を使って停止を試みます。",
			});
			console.error("停止前の進行中ジョブ取得に失敗しました", err);
			if (useChatStore.getState().chatId !== chatId) return;
			jobId = useChatStore.getState().activeAgentJobId;
		}

		if (!jobId) {
			// 進行中ジョブが無い (既に終端)。idle に戻す。
			if (useChatStore.getState().chatId === chatId) useChatStore.getState().setStatus("idle");
			return;
		}

		let job: Awaited<ReturnType<typeof stopAgentJob>>;
		try {
			if (useChatStore.getState().chatId !== chatId) return;
			job = await stopAgentJob(chatId, jobId);
		} catch (err: unknown) {
			useToastStore.getState().show({
				type: "error",
				message: err instanceof Error ? err.message : "処理の停止に失敗しました。",
			});
			// 状態を整えるため進行中ジョブを取り直す。取得できなければ idle に戻す。
			try {
				const active = await getActiveAgentJob(chatId);
				if (useChatStore.getState().chatId !== chatId) return;
				useChatStore.getState().setActiveAgentJob(active?.id ?? null);
				useChatStore
					.getState()
					.setStatus(active?.status === "cancelRequested" ? "stopping" : "idle");
			} catch (refetchErr: unknown) {
				useToastStore.getState().show({
					type: "error",
					message: "停止失敗後の再同期に失敗しました。再読み込みしてください。",
				});
				console.error("停止失敗後の再同期に失敗しました", refetchErr);
				if (useChatStore.getState().chatId === chatId) {
					useChatStore.getState().setStatus("idle");
				}
			}
			throw err;
		}

		if (useChatStore.getState().chatId !== chatId) return;

		if (job.status === "cancelled") {
			queueRetractionRestore(chatId, job.retractedMessage?.text ?? takeLocalStopRestore(chatId));
			// Pending ジョブの即時キャンセル。応答は来ないのでここで取り直して idle へ戻す。
			try {
				const messages = await listChatMessages(chatId);
				if (useChatStore.getState().chatId !== chatId) return;
				useChatStore.getState().setMessages(messages);
			} catch (err: unknown) {
				useToastStore.getState().show({
					type: "error",
					message: "停止完了後のメッセージ取得に失敗しました。再読み込みしてください。",
				});
				throw err;
			}
			const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
			if (environmentId !== null) {
				refreshEnvironmentFiles(environmentId);
				refreshDeviceSharedVolumes(environmentId);
				refreshCloudSharedVolumes(environmentId);
			}
			useChatStore.getState().setStatus("idle");
			useChatStore.getState().setActiveAgentJob(null);
		}
		// cancelRequested は "stopping" のまま。停止完了の systemNotice (SSE) が idle へ戻す。
	}, [takeLocalStopRestore]);

	return {
		startStreaming,
		stopStreaming,
		startNewChatDraft,
		switchToChat,
		stopAgentSession,
	};
}

interface ProcessEventOptions {
	/**
	 * When set, drop the message if the current store chatId no longer matches —
	 * guards against a streaming-loop tail emitting buffered messages after a
	 * chat swap. See issue #60.
	 */
	expectedChatId?: string | null;
	onTurnTerminal?: (message: ChatMessageDto, retracted: RetractedMessage | null) => void;
}

/**
 * Stop reasons that end the agent's turn. `toolUse` and `pauseTurn` mean the
 * agent loop will keep producing messages, so they are *not* terminal.
 */
const TURN_TERMINAL_STOP_REASONS: ReadonlySet<ChatStopReason> = new Set([
	"endTurn",
	"maxTokens",
	"modelContextWindowExceeded",
	"stopSequence",
	"refusal",
]);

const RESUMABLE_AGENT_JOB_STATUSES: ReadonlySet<string> = new Set(["pending", "running"]);

/**
 * 切り替え先のチャットに載せるメタデータ。ChatMetadata はそのまま使う。チャット一覧 (ChatListEntry) は revision と
 * デバイス名を持たないので、revision は暫定 0 を載せ、デバイス名は同じ環境なら引き継ぐ (どちらも後段の loadChat で
 * 取得した値で上書きする)。引き継がないと、同じ環境のチャットを切り替えるたびにデバイスの段が出直す。
 */
export function switchedChatMetadata(
	entry: ChatMetadata | ChatListEntry,
	current: { environmentId: string | null; environmentRunnerName: string | null },
): ChatMetadata {
	if ("revision" in entry) return entry;
	return {
		...entry,
		revision: 0,
		environmentRunnerName:
			entry.environmentId !== null && entry.environmentId === current.environmentId
				? current.environmentRunnerName
				: null,
	};
}

/**
 * Whether a message ends the agent's turn. A message with a non-terminal stop
 * reason (`toolUse`, `pauseTurn`) or no stop reason yet (`null`, e.g. a freshly
 * sent user message awaiting a response) means the turn is still in progress and
 * the stream should be (re)opened to receive the rest.
 */
export function isTurnTerminal(message: ChatMessageDto): boolean {
	return message.stopReason != null && TURN_TERMINAL_STOP_REASONS.has(message.stopReason);
}

export function lastConversationMessage(
	messages: readonly ChatMessageDto[],
): ChatMessageDto | undefined {
	return messages.findLast(
		(message) => message.role !== "monitorEvent" && message.role !== "subagentEvent",
	);
}

export function shouldResumeActiveTurnOnOpen(
	activeJobStatus: string | null | undefined,
	conversationTail: ChatMessageDto | undefined,
): boolean {
	return (
		(activeJobStatus != null && RESUMABLE_AGENT_JOB_STATUSES.has(activeJobStatus)) ||
		(conversationTail !== undefined && !isTurnTerminal(conversationTail))
	);
}

/** エージェントの open_file が成功したファイルを右ペインで開く。失敗はトーストで伝わるのでログだけ残す。 */
function openAgentRequestedFile(path: string): void {
	openEnvironmentFileByPath(path).catch((err: unknown) => {
		console.error("Failed to open file requested by agent", err);
	});
}

/**
 * Dispatch a streamed `ChatMessageDto` into the session store via
 * `upsertStreamedMessage`, which reconciles by `clientRequestId`: the server
 * version of an optimistically-added user message (same `clientRequestId`, new
 * server id) replaces the optimistic entry in place instead of being appended a
 * second time, while id-dedup still drops re-delivered rows. Returns `true` when
 * a message carries a turn-ending `stopReason`, the per-turn terminal signal
 * that lets the live-stream caller abort its per-turn fetch instead of idling on
 * the backend's keepalive loop.
 */
export function processEvent(dto: ChatMessageDto, options: ProcessEventOptions): boolean {
	const { expectedChatId, onTurnTerminal } = options;
	const store = useChatStore.getState();
	if (expectedChatId != null && store.chatId !== expectedChatId) {
		return false;
	}

	// 再配信された行で開き直さないよう、初めて届いた行のときだけ開く。
	const firstDelivery = !store.messages.some((m) => m.id === dto.id);
	const agentFilePath = agentOpenFilePath(dto, store.messages);
	const knowledgeGraphChanged = firstDelivery && agentChangedKnowledgeGraph(dto, store.messages);
	store.upsertStreamedMessage(dto);
	if (firstDelivery && agentFilePath !== null) openAgentRequestedFile(agentFilePath);
	if (knowledgeGraphChanged) useKnowledgeGraphStore.getState().markChanged();

	// 確定した応答が届いたら暫定表示を畳む。React が同一更新へまとめるため二重表示にはならない。
	if (dto.role === "assistant") store.clearStreamingAssistant();

	// クライアント実行の HITL ツール呼び出しが届いたら、対応するパネルを表示して
	// ストリームを終端する。バックエンドのエージェントループはここで一時停止しており、
	// ユーザーの回答 (tool_result) を POST した時に再開する。
	const pendingHITL = findPendingHITL(useChatStore.getState().messages);
	if (pendingHITL) {
		store.setHITL(pendingHITL);
		store.setStatus("idle");
		return true;
	}
	// 別タブや org admin が先に決定した承認は resolved マーカーの受信で閉じる。
	if (useChatStore.getState().hitlState !== null) store.clearHITL();

	// 未応答の HITL が無くなったらパネルを畳む。バックエンドは HITL ツールが他ツールと同一レスポンスに
	// 混在した場合に tool_result で拒否するため、ユーザー操作なしで応答済みになることがある。ここで
	// 畳まないとパネルが残り続け、応答済みの tool_use に対する二重回答が送られてチャットが壊れる。
	if (useChatStore.getState().hitlState) {
		store.clearHITL();
	}

	if (isTurnTerminal(dto)) {
		// systemNotice (停止完了通知) を含むターン終端。進行中ジョブはここで完了しているので
		// 停止ボタンの対象をクリアし、状態を idle に戻す。
		const wasStopping = store.status === "stopping";
		store.clearStreamingAssistant();
		store.setStatus("idle");
		store.setActiveAgentJob(null);
		const retracted = parseRetractedMessage(dto.stopDetails);
		if (wasStopping || retracted !== null) {
			onTurnTerminal?.(dto, retracted);
		}
		return true;
	}

	// systemError / systemNotice / background event / compaction はターンを進めるメッセージではないため streaming へ戻さない。
	// 停止処理中 (stopping) も、遅れて届いた monitorEvent 等で streaming へ戻さない。
	// stopping を idle へ戻すのは停止完了の systemNotice (上の terminal 分岐) だけ。
	if (
		store.status !== "stopping" &&
		dto.role !== "systemError" &&
		dto.role !== "systemNotice" &&
		dto.role !== "monitorEvent" &&
		dto.role !== "compaction" &&
		dto.role !== "subagentEvent"
	) {
		store.setStatus("streaming");
	}

	return false;
}
